/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity */
/* eslint-disable @atlassian/perf-linting/no-chain-state-updates */
/* eslint-disable @atlassian/perf-linting/no-expensive-computations-in-render */
import React, {
	type ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Subscribe } from 'unstated';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import EmptyState from '@atlaskit/empty-state/empty-state';
import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import CrossIcon from '@atlaskit/icon/core/cross';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import UndoIcon from '@atlaskit/icon/core/undo';
import Heading from '@atlaskit/heading/heading';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Pressable from '@atlaskit/primitives/pressable';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import PageIcon from '@atlaskit/icon/core/page';
import DefaultAppIcon from '@atlaskit/icon/core/app';
import { Checkbox } from '@atlaskit/checkbox/checkbox';
import Spinner from '@atlaskit/spinner/spinner';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { IllustrationComponent } from '@atlassian/illustrations/ui';

import { FallbackableImage } from '@confluence/fallbackable-image';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { SuggestionMovePopup } from './SuggestionMovePopup.tsx';
import { SuggestionsFilterPopup } from './SuggestionsFilterPopup.tsx';
import { useContentTitles } from './hooks/useContentTitles.ts';
import { useSubmitSuggestionActions } from './hooks/useSubmitSuggestionActions.ts';
import {
	ALL_EXTERNAL_PRODUCT_CONNECTIONS,
	EXTERNAL_PRODUCT_CONNECTION,
	EXTERNAL_PRODUCT_CONNECTION_ICON_URLS,
	type ExternalProductConnection,
	getSuggestionIconUrlFromContentUrl,
	getSuggestionProductKeyFromContentUrl,
	type Suggestion,
} from './__types__/RemoteObject.ts';
import { useFetchSuggestions } from './hooks/useFetchSuggestions.ts';
import { useReviewSuggestionsExperience } from './hooks/useReviewSuggestionsExperience.ts';
import {
	useReviewSuggestionsAnalytics,
	useCurationErrorStateAnalytics,
	useSuggestionPreviewAnalytics,
	useSuggestionClickAnalytics,
} from './hooks/useAnalytics.ts';
import { getSuggestionReasoningType } from './getSuggestionReasoning.ts';
import { ContentPreviewPlaceholder } from './ContentPreviewPlaceholder.tsx';
import { useCreatorInfo } from './hooks/useCreatorInfo.ts';
import { useLoomThumbnails } from './hooks/useLoomThumbnails.ts';
import previewThumbnailFallback from './img/preview-thumbnail-fallback.svg';
import { SuggestionFeedbackModal } from './SuggestionFeedbackModal.tsx';
import googleDriveIcon from './img/google-drive.svg';
import loomIcon from './img/loom.svg';
import connectArrowIcon from './img/connect-arrow.png';
import previewBackground from './img/preview-background.svg';
import sparkleIcon from './img/content-preview-sparkle.svg';

type PreviewIconSource = 'google-drive' | 'loom' | 'onedrive';

const PRODUCT_DISPLAY_NAMES: Record<PreviewIconSource, string> = {
	'google-drive': 'Google Drive',
	loom: 'Loom',
	onedrive: 'Microsoft OneDrive',
};

const PREVIEW_SOURCE_ICON_URLS: Record<PreviewIconSource, string> = {
	'google-drive': EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE],
	loom: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.LOOM],
	onedrive: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT],
};

const getPreviewSourceFromProductKey = (productKey: string): PreviewIconSource | undefined => {
	switch (productKey) {
		case 'googleDocs':
		case 'googleSheets':
		case 'googleSlides':
		case 'googleDrive':
			return 'google-drive';
		case 'loom':
			return 'loom';
		case 'microsoftTeams':
			return 'onedrive';
		default:
			return undefined;
	}
};

const i18n = defineMessages({
	addItemsToSpace: {
		id: 'space-curation.review-content-suggestions-modal.add-items-to-space',
		defaultMessage: 'Add {count, plural, one {# item} other {# items}} to space',
		description: 'Button label for adding selected content suggestions to space',
	},
	bringTogetherTitle: {
		id: 'space-curation.review-content-suggestions-modal.bring-together-title',
		defaultMessage: 'Bring together the content that matters',
		description: 'Title for the review content suggestions modal',
	},
	bringTogetherDescription: {
		id: 'space-curation.review-content-suggestions-modal.bring-together-description',
		defaultMessage: "Add these suggestions and they'll be organized in this space.",
		description: 'Description for the review content suggestions modal',
	},
	openSuggestion: {
		id: 'space-curation.review-content-suggestions-modal.open-suggestion',
		defaultMessage: 'Open {title}',
		description: 'Aria label for opening a suggestion',
	},
	feedbackButtonLabel: {
		id: 'space-curation.review-content-suggestions-modal.feedback-button-label',
		defaultMessage: 'Give feedback on {title}',
		description: 'Aria label for the feedback button on a suggestion',
	},
	evalButtonLabel: {
		id: 'space-curation.review-content-suggestions-modal.eval-button-label',
		defaultMessage: 'Eval',
		description: 'Label for the eval button on a suggestion',
	},
	untitled: {
		id: 'space-curation.review-content-suggestions-modal.untitled',
		defaultMessage: 'Untitled',
		description: 'Fallback title for a suggestion without a title',
	},
	loading: {
		id: 'space-curation.review-content-suggestions-modal.loading',
		defaultMessage: 'Loading',
		description: 'Loading state label for the add button',
	},
	submitting: {
		id: 'space-curation.review-content-suggestions-modal.submitting',
		defaultMessage: 'Adding...',
		description: 'Submitting state label for the add button',
	},
	rejectSuggestion: {
		id: 'space-curation.review-content-suggestions-modal.reject-suggestion',
		defaultMessage: 'Reject {title}',
		description: 'Aria label for the thumbs down button to reject a suggestion',
	},
	resetSuggestion: {
		id: 'space-curation.review-content-suggestions-modal.reset-suggestion',
		defaultMessage: 'Restore suggestion',
		description: 'Aria label for the undo button to restore a rejected suggestion',
	},
	addToThisSpace: {
		id: 'space-curation.review-content-suggestions-modal.add-to-this-space',
		defaultMessage: 'Add to this space',
		description: 'Disabled button label when no items are selected or thumbed down',
	},
	socialSignalRelatedPage: {
		id: 'space-curation.review-content-suggestions-modal.social-signal-related-page',
		defaultMessage: 'Related to <b>{pageName}</b>',
		description:
			'Social signal indicating the suggested item is related to an existing Confluence page. The page name is rendered with medium emphasis.',
	},
	rejectSuggestionTooltip: {
		id: 'space-curation.review-content-suggestions-modal.reject-suggestion-tooltip',
		defaultMessage: 'Reject suggestion',
		description: 'Tooltip for the thumbs down button to reject a suggestion',
	},
	uncategorized: {
		id: 'space-curation.review-content-suggestions-modal.uncategorized',
		defaultMessage: 'Uncategorized',
		description: 'Group header for suggestions with no assigned parent folder',
	},
	closeModal: {
		id: 'space-curation.review-content-suggestions-modal.close-modal',
		defaultMessage: 'Close',
		description:
			'Tooltip and accessible label for the close button in the review content suggestions modal',
	},
	topRelevantSuggestions: {
		id: 'space-curation.review-content-suggestions-modal.top-relevant-suggestions',
		defaultMessage: 'Top 5 most relevant suggestions',
		description: 'Header label shown above the list of suggestion groups',
	},
	filterEmptyTitle: {
		id: 'space-curation.review-content-suggestions-modal.filter-empty-title',
		defaultMessage: 'No suggestions match your filters',
		description:
			'Heading shown in the suggestions list when the selected source filters return no results',
	},
	filterEmptyDescription: {
		id: 'space-curation.review-content-suggestions-modal.filter-empty-description',
		defaultMessage: 'Try removing your source filters to see all available suggestions.',
		description:
			'Body text shown in the suggestions list when the selected source filters return no results',
	},
	filterEmptyClearButton: {
		id: 'space-curation.review-content-suggestions-modal.filter-empty-clear-button',
		defaultMessage: 'Remove filters',
		description:
			'Button that clears the source filters and re-shows all suggestions in the empty filter state',
	},
	errorStateHeader: {
		id: 'space-curation.review-content-suggestions-modal.error-state-header',
		defaultMessage: 'Something went wrong',
		description:
			'Heading shown in the error state of the review content suggestions modal when no suggestions could be loaded.',
	},
	errorStateDescription: {
		id: 'space-curation.review-content-suggestions-modal.error-state-description',
		defaultMessage:
			"We couldn't load suggestions for this space. Try again, or check your network connection.",
		description:
			'Description shown in the error state of the review content suggestions modal when no suggestions could be loaded.',
	},
	errorStateRetryButton: {
		id: 'space-curation.review-content-suggestions-modal.error-state-retry-button',
		defaultMessage: 'Try again',
		description:
			'Label for the primary button in the error state of the review content suggestions modal. When clicked, it retries loading the suggestions.',
	},
});

const MODAL_WIDTH = 'calc(100vw - 192px)';
const MODAL_HEIGHT = 'calc(100vh - 128px)';
const MODAL_MAX_WIDTH = '1280px';
const MODAL_MAX_HEIGHT = '720px';
const styles = cssMap({
	layout: {
		// @ts-expect-error -- xcss types reject calc() in width/height
		width: MODAL_WIDTH,
		// @ts-expect-error -- xcss types reject calc() in width/height
		height: MODAL_HEIGHT,
		maxWidth: MODAL_MAX_WIDTH,
		maxHeight: MODAL_MAX_HEIGHT,
		borderRadius: token('radius.xlarge'),
		overflow: 'hidden',
	},
	leftPanel: {
		flexShrink: 0,
		flexBasis: '50%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		paddingBlockStart: token('space.600'),
		paddingBlockEnd: token('space.600'),
		paddingInlineStart: token('space.600'),
		paddingInlineEnd: token('space.600'),
		overflow: 'hidden',
	},
	rightPanel: {
		flexGrow: 1,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: token('elevation.surface'),
		overflow: 'hidden',
		position: 'relative',
		borderStartEndRadius: token('radius.xlarge'),
		borderEndEndRadius: token('radius.xlarge'),
		paddingInlineStart: token('space.600'),
	},
	closeButtonWrapper: {
		position: 'absolute',
		top: token('space.200'),
		right: token('space.200'),
		zIndex: 800,
	},
	groupListScrollArea: {
		flexGrow: 1,
		overflowY: 'auto',
		scrollbarWidth: 'none',
		minHeight: '0%',
	},
	groupCard: {
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
	},
	groupRow: {
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.0'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		gap: token('space.100'),
	},
	groupIconSlot: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	groupTitleWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		overflow: 'hidden',
	},
	itemsList: {
		overflow: 'visible',
		minHeight: '0%',
		paddingInline: token('space.050'),
		paddingBlockEnd: token('space.050'),
	},
	itemRowContainer: {
		position: 'relative',
		zIndex: 0,
		width: '100%',
		transition: 'background-color 200ms ease',
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	itemRowContainerHovered: {
		zIndex: 1,
	},
	suggestionLinkWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.150'),
	},
	suggestionLink: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		flexWrap: 'nowrap',
		minWidth: '0%',
		textDecoration: 'none',
		color: token('color.text'),
		flexGrow: 1,
		'&:hover': {
			textDecoration: 'underline',
			textDecorationColor: token('color.text'),
		},
	},
	suggestionDescription: {
		flexGrow: 1,
		minWidth: '0%',
		overflow: 'hidden',
	},
	suggestionReasonCallout: {
		marginBlockStart: token('space.050'),
		width: '100%',
		borderRadius: token('radius.large'),
		alignItems: 'flex-start',
		gap: token('space.100'),
	},
	suggestionReasonIcon: {
		display: 'flex',
		flexShrink: 0,
		marginBlockStart: token('space.050'),
	},
	suggestionIconSlot: {
		width: '40px',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral'),
	},
	suggestionTitleWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		overflow: 'hidden',
	},
	leftPanelHeader: {
		paddingTop: token('space.100'),
	},
	contentPreviewContainer: {
		display: 'flex',
		flexGrow: 1,
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		position: 'relative',
		overflow: 'hidden',
	},
	contentPreviewUnit: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		height: '520px',
	},
	contentPreviewDecorativeBackground: {
		position: 'absolute',
		top: '0',
		left: '0',
		right: '0',
		bottom: '0',
		overflow: 'hidden',
	},
	contentPreviewIllustration: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	contentPreviewIconTopLeft: {
		position: 'absolute',
		// @ts-expect-error
		top: '30px',
		// @ts-expect-error
		left: '-54px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.100'),
		// @ts-expect-error
		zIndex: 'modal',
		transition: 'opacity 200ms ease',
	},
	contentPreviewIconBottomRight: {
		position: 'absolute',
		// @ts-expect-error
		bottom: '40px',
		// @ts-expect-error
		right: '-46px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		// @ts-expect-error
		zIndex: 'modal',
		transition: 'opacity 200ms ease',
	},
	contentPreviewIconVisible: {
		opacity: 1,
	},
	contentPreviewIconHidden: {
		opacity: 0,
		pointerEvents: 'none',
	},
	contentPreviewSparkle: {
		position: 'absolute',
		// @ts-expect-error
		top: '-54px',
		// @ts-expect-error
		right: '-46px',
		transform: 'rotate(-8.497deg)',
		// @ts-expect-error
		zIndex: 'modal',
	},
	contentPreviewGoogleDriveIcon: {
		transform: 'rotate(-15deg)',
		// @ts-expect-error
		border: `2px solid ${token('color.border')}`,
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		width: '36px',
		height: '36px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	},
	contentPreviewLoomIcon: {
		transform: 'rotate(15deg)',
		// @ts-expect-error -- eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginInlineStart: '64px', // eslint-disable-line @atlaskit/design-system/use-tokens-space
	},
	contentPreviewArrowTopLeft: {
		transform: 'scaleY(-1) rotate(150deg)',
		// @ts-expect-error -- eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginInlineEnd: '-38px', // eslint-disable-line @atlaskit/design-system/use-tokens-space
	},
	contentPreviewDarkBlueBox: {
		position: 'absolute',
		width: '316px',
		height: '253px',
		backgroundColor: token('color.background.brand.bold'),
		transform: 'rotate(-1.25deg)',
		// @ts-expect-error
		bottom: '-10px',
		// @ts-expect-error
		left: '-10px',
	},
	contentPreviewCard: {
		display: 'flex',
		flexDirection: 'column',
		width: '328px',
		height: '368px',
		backgroundColor: token('elevation.surface'),
		position: 'relative',
		overflow: 'visible',
	},
	contentPreviewImageContainer: {
		width: '100%',
		maxHeight: '165px',
		overflow: 'hidden',
		flexShrink: 0,
	},
	contentPreviewImageFallback: {
		width: '100%',
		height: '130px',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	contentPreviewProductLabel: {
		flexShrink: 0,
		marginBlockStart: 'auto',
	},
	contentPreviewCardPadding: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		padding: token('space.250'),
		paddingBlockEnd: token('space.200'),
		width: '100%',
		overflow: 'hidden',
	},
	itemRow: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		gap: token('space.050'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	unselectedLink: {
		'&:hover': {
			textDecoration: 'none',
		},
	},
	loadingOverlay: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		// @ts-expect-error -- xcss types reject calc() in width/height
		width: MODAL_WIDTH,
		// @ts-expect-error -- xcss types reject calc() in width/height
		height: MODAL_HEIGHT,
		maxWidth: MODAL_MAX_WIDTH,
		maxHeight: MODAL_MAX_HEIGHT,
	},
	errorState: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		// @ts-expect-error -- xcss types reject calc() in width/height
		width: MODAL_WIDTH,
		// @ts-expect-error -- xcss types reject calc() in width/height
		height: MODAL_HEIGHT,
		maxWidth: MODAL_MAX_WIDTH,
		maxHeight: MODAL_MAX_HEIGHT,
	},
	feedbackIconButton: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	thumbsDownButton: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	itemRowContainerRejected: {
		backgroundColor: token('color.background.disabled'),
	},
	suggestionsListHeader: {
		paddingInlineStart: token('space.0'),
		// The filter button sits at the top-right corner of the scrollable area, whose overflow
		// clips both axes. Add clearance so its focus ring isn't cut off at the top/right edges.
		paddingBlockStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
	},
	filterEmptyState: {
		paddingBlock: token('space.400'),
		paddingInline: token('space.200'),
		textAlign: 'center',
	},
});

type SuggestionItemRowProps = {
	suggestion: Suggestion;
	hasFeedbackBeenSubmitted?: boolean;
	onFeedbackSubmitted?: (suggestionId: string) => void;
	isSelected: boolean;
	isRejected: boolean;
	spaceId?: string;
	spaceKey?: string;
	onToggleSelectItem: (id: string) => void;
	onRejectItem: (id: string) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
	onSuggestionHover?: (suggestion: Suggestion | null) => void;
	onSuggestionClick?: (suggestion: Suggestion) => void;
	titlesByContentId: Map<string, string>;
};

const SuggestionItemRow = ({
	spaceId,
	spaceKey,
	suggestion,
	isSelected,
	isRejected,
	hasFeedbackBeenSubmitted,
	onFeedbackSubmitted,
	onToggleSelectItem,
	onRejectItem,
	onMoveSuggestion,
	onSuggestionHover,
	onSuggestionClick,
	titlesByContentId,
}: SuggestionItemRowProps) => {
	const { formatMessage } = useIntl();
	const iconUrl = getSuggestionIconUrlFromContentUrl(suggestion.url);
	// Tracks immediate mouse-over state for row UI elements (Move button, row highlight)
	const [isRowHovered, setIsRowHovered] = useState(false);
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
	const [isMovePopupOpen, setIsMovePopupOpen] = useState(false);

	const { reasonLabel, hasReason } = useMemo((): {
		reasonLabel: ReactNode;
		hasReason: boolean;
	} => {
		const { explanation, relatedPages } = suggestion;
		const firstRelatedPageId = relatedPages?.[0];
		const relatedPageTitle = firstRelatedPageId
			? (titlesByContentId.get(String(firstRelatedPageId)) ?? '')
			: '';

		// Reuse the shared resolver so the reason shown here matches the reasoningType logged in analytics.
		const choice = getSuggestionReasoningType(suggestion, titlesByContentId);

		if (choice === null) {
			return { reasonLabel: null, hasReason: false };
		}

		if (choice === 'related') {
			return {
				reasonLabel: formatMessage(i18n.socialSignalRelatedPage, {
					pageName: relatedPageTitle,
					b: (chunks: ReactNode[]) => (
						<Text
							weight="medium"
							size="small"
							color={isRejected ? 'color.text.disabled' : undefined}
						>
							{chunks}
						</Text>
					),
				}),
				hasReason: true,
			};
		}

		return { reasonLabel: explanation, hasReason: true };
	}, [suggestion, titlesByContentId, formatMessage, isRejected]);

	// Clamp the explanation to the number of lines it occupies at rest, so it truncates (rather
	// than wrapping and growing the row) when hover buttons narrow the callout.
	const descRef = useRef<HTMLDivElement>(null);
	const [descLineCount, setDescLineCount] = useState<number | undefined>(undefined);

	useLayoutEffect(() => {
		// Skip while narrowed (hovered / move-popup open) so the clamp reflects the at-rest width.
		if (!hasReason || isRowHovered || isMovePopupOpen) {
			return;
		}
		const el = descRef.current;
		if (!el) {
			return;
		}
		const measure = () => {
			// Line-height comes from the Text (its small-size metrics), not the wrapper Box.
			const textEl = el.firstElementChild ?? el;
			const computed = getComputedStyle(textEl);
			let lineHeight = parseFloat(computed.lineHeight);
			if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
				lineHeight = (parseFloat(computed.fontSize) || 16) * 1.4;
			}
			setDescLineCount(Math.max(1, Math.round(el.scrollHeight / lineHeight)));
		};
		measure();
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		const resizeObserver = new ResizeObserver(measure);
		resizeObserver.observe(el);
		return () => resizeObserver.disconnect();
	}, [hasReason, reasonLabel, isRowHovered, isMovePopupOpen]);

	const handleShow = () => {
		if (isRejected) return;
		setIsRowHovered(true);
		onSuggestionHover?.(suggestion);
	};

	const handleHide = () => {
		if (isRejected) return;
		setIsRowHovered(false);
	};

	const handleMovePopupOpenChange = (open: boolean) => {
		setIsMovePopupOpen(open);
		if (open) {
			handleHide();
		}
	};

	useEffect(() => {
		if (!isSelected) {
			handleHide();
			handleMovePopupOpenChange(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSelected]);

	return (
		<Box
			xcss={cx(
				styles.itemRowContainer,
				!isRejected && isRowHovered && styles.itemRowContainerHovered,
				isRejected && styles.itemRowContainerRejected,
			)}
			onMouseEnter={handleShow}
			onMouseLeave={handleHide}
			onFocusCapture={handleShow}
			onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
				if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
				handleHide();
			}}
			testId={`review-content-suggestion-row-${suggestion.id}`}
		>
			<Flex alignItems="center" xcss={styles.itemRow}>
				<Box xcss={styles.suggestionLinkWrapper}>
					<Box
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={isRejected ? { opacity: 0.5 } : undefined}
						xcss={styles.suggestionIconSlot}
						aria-hidden="true"
					>
						{iconUrl ? (
							<FallbackableImage
								src={iconUrl}
								width="20"
								height="20"
								fallback={() => <DefaultAppIcon label="" />}
								role="presentation"
							/>
						) : (
							<DefaultAppIcon label="" color={token('color.icon.subtle')} />
						)}
					</Box>
					<Anchor
						href={suggestion.url}
						target="_blank"
						rel="noopener noreferrer"
						aria-label={formatMessage(i18n.openSuggestion, { title: suggestion.title })}
						xcss={cx(styles.suggestionLink, !isSelected && styles.unselectedLink)}
						onClick={() => onSuggestionClick?.(suggestion)}
					>
						<Box xcss={styles.suggestionTitleWrapper}>
							<Text maxLines={1} color={isRejected ? 'color.text.disabled' : undefined}>
								{suggestion.title}
							</Text>
						</Box>
						{hasReason && (
							<Flex xcss={styles.suggestionReasonCallout}>
								<Box xcss={styles.suggestionReasonIcon}>
									<AiSparkleIcon
										label=""
										size="small"
										color={isRejected ? token('color.icon.disabled') : token('color.icon.subtlest')}
									/>
								</Box>
								<Box ref={descRef} xcss={styles.suggestionDescription}>
									<Text
										size="small"
										color={isRejected ? 'color.text.disabled' : 'color.text.subtle'}
										maxLines={descLineCount}
									>
										{reasonLabel}
									</Text>
								</Box>
							</Flex>
						)}
					</Anchor>
				</Box>
				{!hasFeedbackBeenSubmitted &&
					expVal('cc-space-curation-feedback-survey', 'isEnabled', false) && (
						<Pressable
							xcss={styles.feedbackIconButton}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								handleHide();
								setIsFeedbackModalOpen(true);
							}}
							aria-label={formatMessage(i18n.feedbackButtonLabel, { title: suggestion.title })}
						>
							<Text size="small" color="color.text.subtle">
								{formatMessage(i18n.evalButtonLabel)}
							</Text>
						</Pressable>
					)}
				{!isRejected && (isRowHovered || isMovePopupOpen) && (
					<Inline space="space.050" alignBlock="center">
						<SuggestionMovePopup
							suggestionId={suggestion.id}
							spaceKey={spaceKey}
							isOpen={isMovePopupOpen}
							onOpenChange={handleMovePopupOpenChange}
							onMoveSuggestion={onMoveSuggestion}
							fallbackParents={suggestion.fallbackParents}
							currentParentId={suggestion.parentId ?? undefined}
						/>
						<Tooltip content={formatMessage(i18n.rejectSuggestionTooltip)}>
							<Pressable
								xcss={styles.thumbsDownButton}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									onRejectItem(suggestion.id);
								}}
								aria-label={formatMessage(i18n.rejectSuggestion, { title: suggestion.title })}
							>
								<ThumbsDownIcon label="" color={token('color.icon.subtle')} />
							</Pressable>
						</Tooltip>
					</Inline>
				)}
				{isRejected && (
					<Box padding="space.050">
						<IconButton
							icon={UndoIcon}
							label={formatMessage(i18n.resetSuggestion, { title: suggestion.title })}
							appearance="subtle"
							spacing="compact"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								onRejectItem(suggestion.id);
							}}
						/>
					</Box>
				)}
				{!isRejected && (
					<Box padding="space.050">
						<Checkbox isChecked={isSelected} onChange={() => onToggleSelectItem(suggestion.id)} />
					</Box>
				)}
			</Flex>
			<ModalTransition>
				{isFeedbackModalOpen && expVal('cc-space-curation-feedback-survey', 'isEnabled', false) && (
					<SuggestionFeedbackModal
						suggestion={suggestion}
						spaceId={spaceId}
						spaceKey={spaceKey}
						onClose={() => {
							setIsFeedbackModalOpen(false);
						}}
						onSubmit={() => onFeedbackSubmitted?.(suggestion.id)}
					/>
				)}
			</ModalTransition>
		</Box>
	);
};

type SuggestionGroupRowProps = {
	parentTitle: string;
	onSuggestionHover?: (suggestion: Suggestion | null) => void;
	onSuggestionClick?: (suggestion: Suggestion) => void;
	parentType?: string;
	suggestions: Suggestion[];
	isGroupSelected: boolean;
	selectedItems: Set<string>;
	rejectedItems: Set<string>;
	parentId: number | null;
	toggleSelectGroup: (groupSuggestions: Suggestion[]) => void;
	onToggleSelectItem: (id: string) => void;
	onRejectItem: (id: string) => void;
	onGroupHoverChange: (parentId: number | null, isHovering: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
	spaceId?: string;
	submittedFeedbackIds: Set<string>;
	onFeedbackSubmitted: (suggestionId: string) => void;
	spaceKey?: string;
	titlesByContentId: Map<string, string>;
};

const SuggestionGroupRow = ({
	parentTitle,
	parentType,
	suggestions,
	isGroupSelected,
	selectedItems,
	rejectedItems,
	spaceId,
	spaceKey,
	parentId,
	toggleSelectGroup,
	onToggleSelectItem,
	onRejectItem,
	onGroupHoverChange,
	submittedFeedbackIds,
	onFeedbackSubmitted,
	onMoveSuggestion,
	onSuggestionHover,
	onSuggestionClick,
	titlesByContentId,
}: SuggestionGroupRowProps) => {
	return (
		<Box
			xcss={styles.groupCard}
			onMouseEnter={isGroupSelected ? () => onGroupHoverChange(parentId, true) : undefined}
			onMouseLeave={isGroupSelected ? () => onGroupHoverChange(parentId, false) : undefined}
			onFocusCapture={isGroupSelected ? () => onGroupHoverChange(parentId, true) : undefined}
			onBlur={
				isGroupSelected
					? (event: React.FocusEvent<HTMLDivElement>) => {
							if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
								onGroupHoverChange(parentId, false);
							}
						}
					: undefined
			}
			testId={`review-content-suggestion-group-card-${parentId}`}
		>
			<Flex alignItems="center" xcss={styles.groupRow}>
				<Inline alignBlock="center" shouldWrap={false} grow="fill" space="space.075">
					<Box xcss={styles.groupIconSlot}>
						{parentType === 'page' ? (
							<PageIcon label="" color={token('color.icon.subtle')} />
						) : (
							<FolderOpenIcon label="" color={token('color.icon.subtle')} />
						)}
					</Box>
					<Box xcss={styles.groupTitleWrapper}>
						<Text weight="medium" maxLines={1}>
							{parentTitle}
						</Text>
					</Box>
				</Inline>
				<Box padding="space.050">
					<Checkbox isChecked={isGroupSelected} onChange={() => toggleSelectGroup(suggestions)} />
				</Box>
			</Flex>
			<Box xcss={styles.itemsList}>
				{suggestions.map((suggestion) => (
					<SuggestionItemRow
						key={suggestion.id}
						suggestion={suggestion}
						isSelected={selectedItems.has(suggestion.id)}
						isRejected={rejectedItems.has(suggestion.id)}
						spaceId={spaceId}
						hasFeedbackBeenSubmitted={submittedFeedbackIds.has(suggestion.id)}
						onFeedbackSubmitted={onFeedbackSubmitted}
						spaceKey={spaceKey}
						onToggleSelectItem={onToggleSelectItem}
						onRejectItem={onRejectItem}
						onMoveSuggestion={onMoveSuggestion}
						onSuggestionHover={onSuggestionHover}
						onSuggestionClick={onSuggestionClick}
						titlesByContentId={titlesByContentId}
					/>
				))}
			</Box>
		</Box>
	);
};

type ModalLeftPanelProps = {
	groupedSuggestionsWithTitles: Map<number | null, ParentGrouping>;
	contentMetaById: Map<string, { title: string; type: string }>;
	selectedItems: Set<string>;
	rejectedItems: Set<string>;
	toggleSelectGroup: (groupSuggestions: Suggestion[]) => void;
	toggleSelectItem: (itemId: string) => void;
	onRejectItem: (itemId: string) => void;
	onGroupHoverChange: (parentId: number | null, isHovering: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
	onSuggestionHover?: (suggestion: Suggestion | null) => void;
	onSuggestionClick?: (suggestion: Suggestion) => void;
	spaceId?: string;
	spaceKey?: string;
	titlesByContentId: Map<string, string>;
	onFilterClose: (sources: ExternalProductConnection[]) => void;
	filterResetKey: number;
	isFilteredEmpty: boolean;
	onClearFilters: () => void;
};

const ModalLeftPanel = ({
	groupedSuggestionsWithTitles,
	contentMetaById,
	selectedItems,
	rejectedItems,
	toggleSelectGroup,
	toggleSelectItem,
	onRejectItem,
	onGroupHoverChange,
	onMoveSuggestion,
	onSuggestionHover,
	onSuggestionClick,
	spaceId,
	spaceKey,
	titlesByContentId,
	onFilterClose,
	filterResetKey,
	isFilteredEmpty,
	onClearFilters,
}: ModalLeftPanelProps) => {
	const { formatMessage } = useIntl();
	const [submittedFeedbackIds, setSubmittedFeedbackIds] = useState<Set<string>>(new Set());

	const handleFeedbackSubmitted = useCallback((suggestionId: string) => {
		setSubmittedFeedbackIds((prev) => new Set([...prev, suggestionId]));
	}, []);

	return (
		<Stack space="space.150">
			{expVal('confluence_autonomous_space_curation_filtering', 'isEnabled', false) && (
				<Flex
					alignItems="center"
					justifyContent="space-between"
					xcss={styles.suggestionsListHeader}
					testId="suggestions-list-header"
				>
					<Text size="medium" weight="medium">
						{formatMessage(i18n.topRelevantSuggestions)}
					</Text>
					<SuggestionsFilterPopup key={filterResetKey} onClose={onFilterClose} />
				</Flex>
			)}
			{isFilteredEmpty ? (
				<Box xcss={styles.filterEmptyState} testId="suggestions-filter-empty-state">
					<Stack space="space.100" alignInline="center">
						<Text weight="bold">{formatMessage(i18n.filterEmptyTitle)}</Text>
						<Text color="color.text.subtlest">{formatMessage(i18n.filterEmptyDescription)}</Text>
						<Button
							appearance="subtle"
							onClick={onClearFilters}
							testId="suggestions-filter-empty-clear-button"
						>
							{formatMessage(i18n.filterEmptyClearButton)}
						</Button>
					</Stack>
				</Box>
			) : (
				[...groupedSuggestionsWithTitles.entries()].map(
					([parentId, { parentTitle, suggestions: groupSuggestions }]) => {
						const isGroupSelected = groupSuggestions.some((s) => selectedItems.has(s.id));
						const parentType =
							parentId !== null ? contentMetaById.get(String(parentId))?.type : undefined;
						return (
							<SuggestionGroupRow
								key={String(parentId)}
								parentTitle={
									parentTitle ||
									formatMessage(parentId === null ? i18n.uncategorized : i18n.untitled)
								}
								parentType={parentType}
								suggestions={groupSuggestions}
								isGroupSelected={isGroupSelected}
								selectedItems={selectedItems}
								rejectedItems={rejectedItems}
								parentId={parentId}
								toggleSelectGroup={toggleSelectGroup}
								onToggleSelectItem={toggleSelectItem}
								onRejectItem={onRejectItem}
								onGroupHoverChange={onGroupHoverChange}
								onMoveSuggestion={onMoveSuggestion}
								onSuggestionHover={onSuggestionHover}
								onSuggestionClick={onSuggestionClick}
								spaceId={spaceId}
								submittedFeedbackIds={submittedFeedbackIds}
								onFeedbackSubmitted={handleFeedbackSubmitted}
								spaceKey={spaceKey}
								titlesByContentId={titlesByContentId}
							/>
						);
					},
				)
			)}
		</Stack>
	);
};

type ParentGrouping = {
	parentTitle: string;
	suggestions: Suggestion[];
};

const PreviewContent = ({
	imageUrl,
	imageLoading,
	title,
	description,
	productIconUrl,
	authorName,
	authorAvatarUrl,
}: {
	imageUrl?: string;
	imageLoading?: boolean;
	title?: string;
	description?: string;
	productIconUrl?: string;
	authorName?: string;
	authorAvatarUrl?: string;
}) => {
	const [imageError, setImageError] = useState(false);
	const [avatarError, setAvatarError] = useState(false);
	const [imageLoaded, setImageLoaded] = useState(false);

	return (
		<Stack space="space.150">
			<Box xcss={styles.contentPreviewImageContainer}>
				{!imageLoading && (!imageUrl || imageError) && (
					<Box xcss={styles.contentPreviewImageFallback}>
						<img src={previewThumbnailFallback} alt="" aria-hidden="true" width="100%" />
					</Box>
				)}
				{imageUrl && !imageError && (
					<img
						src={imageUrl}
						alt=""
						width="100%"
						onLoad={() => setImageLoaded(true)}
						onError={() => setImageError(true)}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={{ display: imageLoaded ? 'block' : 'none' }}
					/>
				)}
			</Box>
			{title && (
				<Text weight="medium" maxLines={2}>
					{productIconUrl && (
						<img
							src={productIconUrl}
							alt=""
							width="16"
							height="16"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- inline img alignment can't use xcss
							style={{ verticalAlign: '-3px', marginRight: '6px' }}
						/>
					)}
					{title}
				</Text>
			)}

			{authorName && (
				<Inline space="space.050" alignBlock="center">
					{authorAvatarUrl && !avatarError && (
						<img
							src={authorAvatarUrl}
							alt=""
							width="16"
							height="16"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={{ borderRadius: '50%' }}
							onError={() => setAvatarError(true)}
						/>
					)}
					<Text size="small" color="color.text.subtlest">
						{authorName}
					</Text>
				</Inline>
			)}
			{description && (
				<Text size="small" color="color.text.subtlest" maxLines={3}>
					{description}
				</Text>
			)}
		</Stack>
	);
};

const ModalRightPanel = ({
	previewSource,
	previewImageUrl,
	previewImageLoading,
	previewTitle,
	previewDescription,
	previewProductIconUrl,
	previewSourceIconUrl,
	previewAuthorName,
	previewAuthorAvatarUrl,
	onClose,
}: {
	previewSource?: PreviewIconSource;
	previewImageUrl?: string;
	previewImageLoading?: boolean;
	previewTitle?: string;
	previewDescription?: string;
	previewProductIconUrl?: string;
	previewSourceIconUrl?: string;
	previewAuthorName?: string;
	previewAuthorAvatarUrl?: string;
	onClose: () => void;
}) => {
	const { formatMessage } = useIntl();
	const showGoogleDrive = !previewSource || previewSource === 'google-drive';
	const showLoom = !previewSource || previewSource === 'loom';

	return (
		<Box xcss={styles.rightPanel}>
			<Box xcss={styles.closeButtonWrapper}>
				<IconButton
					icon={CrossIcon}
					label={formatMessage(i18n.closeModal)}
					appearance="subtle"
					onClick={onClose}
					isTooltipDisabled={false}
				/>
			</Box>
			<Box xcss={styles.contentPreviewContainer}>
				<Box xcss={styles.contentPreviewUnit}>
					<Box xcss={styles.contentPreviewDecorativeBackground}>
						<img src={previewBackground} alt="" aria-hidden="true" />
					</Box>
					<Box xcss={styles.contentPreviewIllustration}>
						<Box xcss={styles.contentPreviewDarkBlueBox} />
						<Box xcss={styles.contentPreviewCard}>
							<Box xcss={styles.contentPreviewSparkle}>
								<img src={sparkleIcon} alt="" aria-hidden="true" width="75" height="83" />
							</Box>
							<Box
								xcss={cx(
									styles.contentPreviewIconTopLeft,
									showGoogleDrive
										? styles.contentPreviewIconVisible
										: styles.contentPreviewIconHidden,
								)}
							>
								<Box xcss={styles.contentPreviewGoogleDriveIcon}>
									<img src={googleDriveIcon} alt="" aria-hidden="true" width="32" height="32" />
								</Box>
								<Box xcss={styles.contentPreviewArrowTopLeft}>
									<img src={connectArrowIcon} alt="" aria-hidden="true" width="61" height="36" />
								</Box>
							</Box>
							<Box
								xcss={cx(
									styles.contentPreviewIconBottomRight,
									showLoom ? styles.contentPreviewIconVisible : styles.contentPreviewIconHidden,
								)}
							>
								<Box xcss={styles.contentPreviewLoomIcon}>
									<img src={loomIcon} alt="" aria-hidden="true" width="32" height="32" />
								</Box>
								<img src={connectArrowIcon} alt="" aria-hidden="true" width="61" height="36" />
							</Box>
							<Box xcss={styles.contentPreviewCardPadding}>
								{previewSource ? (
									<PreviewContent
										key={previewImageUrl || previewTitle}
										imageUrl={previewImageUrl}
										imageLoading={previewImageLoading}
										title={previewTitle}
										description={previewDescription}
										productIconUrl={previewProductIconUrl}
										authorName={previewAuthorName}
										authorAvatarUrl={previewAuthorAvatarUrl}
									/>
								) : (
									<ContentPreviewPlaceholder />
								)}
								{previewSource && (
									<Box xcss={styles.contentPreviewProductLabel}>
										<Inline space="space.050" alignBlock="center">
											<img
												src={
													previewSourceIconUrl ||
													(previewSource === 'loom' ? loomIcon : googleDriveIcon)
												}
												alt=""
												width="16"
												height="16"
											/>
											<Text size="small" color="color.text.subtlest">
												{PRODUCT_DISPLAY_NAMES[previewSource]}
											</Text>
										</Inline>
									</Box>
								)}
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

const ModalErrorState = ({
	onClose,
	onRetry,
	spaceId,
	spaceKey,
	reason,
	openTimestamp,
}: {
	onClose: () => void;
	onRetry: () => void;
	spaceId?: string;
	spaceKey?: string;
	reason: 'empty' | 'notProvisioned';
	openTimestamp: number;
}) => {
	const { formatMessage } = useIntl();

	useCurationErrorStateAnalytics({ spaceId, spaceKey, reason, openTimestamp });

	return (
		<Box xcss={styles.errorState} testId="review-content-suggestions-modal-error-state">
			<Box xcss={styles.closeButtonWrapper}>
				<IconButton
					icon={CrossIcon}
					label={formatMessage(i18n.closeModal)}
					appearance="subtle"
					onClick={onClose}
					isTooltipDisabled={false}
				/>
			</Box>
			<EmptyState
				header={formatMessage(i18n.errorStateHeader)}
				description={formatMessage(i18n.errorStateDescription)}
				primaryAction={
					<Button appearance="primary" onClick={onRetry}>
						{formatMessage(i18n.errorStateRetryButton)}
					</Button>
				}
				renderImage={() => (
					<IllustrationComponent illustrationKey="ErrorGeneral" alt="" width={160} height={160} />
				)}
			/>
		</Box>
	);
};

type ReviewContentSuggestionsModalInnerProps = ReviewContentSuggestionsModalProps & {
	flags: FlagsStateContainer;
};

export const ReviewContentSuggestionsModalInner = ({
	spaceKey,
	spaceId,
	modalOpenTimestamp,
	onClose,
	onSaveSuccess,
	flags,
}: ReviewContentSuggestionsModalInnerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();

	const [appliedSources, setAppliedSources] = useState<ExternalProductConnection[]>(() => [
		...ALL_EXTERNAL_PRODUCT_CONNECTIONS,
	]);
	const [filterResetKey, setFilterResetKey] = useState(0);
	const [isReapplyingFilters, setIsReapplyingFilters] = useState(false);
	const isFilterApplied = appliedSources.length < ALL_EXTERNAL_PRODUCT_CONNECTIONS.length;

	const handleFilterPopupClose = useCallback((sources: ExternalProductConnection[]) => {
		setAppliedSources(sources);
	}, []);

	const clearFilters = useCallback(() => {
		setIsReapplyingFilters(true);
		setAppliedSources([...ALL_EXTERNAL_PRODUCT_CONNECTIONS]);
		setFilterResetKey((key) => key + 1);
	}, []);

	const {
		groups,
		isLoading: isFetchingSuggestions,
		isSuppressed,
		refetch,
	} = useFetchSuggestions(
		spaceKey,
		expVal('confluence_autonomous_space_curation_filtering', 'isEnabled', false)
			? appliedSources
			: undefined,
	);
	const reviewSuggestionsExperience = useReviewSuggestionsExperience();

	const [suggestionOverrides, setSuggestionOverrides] = useState<Map<string, { parentId: number }>>(
		() => new Map(),
	);

	const allSuggestions = useMemo(
		() =>
			groups
				.flatMap((g) => g.remoteObjects)
				.map((s) => {
					const override = suggestionOverrides.get(s.id);
					return override ? { ...s, ...override } : s;
				}),
		[groups, suggestionOverrides],
	);

	const { cloudId, activationId } = useSessionData();
	const { thumbnailsByEntityId: loomThumbnails, isLoading: isLoomLoading } = useLoomThumbnails(
		allSuggestions,
		cloudId,
		activationId,
	);

	const [selectedItems, setSelectedItems] = useState<Set<string>>(() => new Set());
	const [rejectedItems, setRejectedItems] = useState<Set<string>>(() => new Set());
	const [_isHoveringGroupList, setIsHoveringGroupList] = useState(false);
	const [_hoveredParentId, setHoveredParentId] = useState<number | null>(null);
	const [hoveredSuggestion, setHoveredSuggestion] = useState<Suggestion | null>(null);
	const previewSource = hoveredSuggestion
		? getPreviewSourceFromProductKey(getSuggestionProductKeyFromContentUrl(hoveredSuggestion.url))
		: undefined;
	const [previewParentId, setPreviewParentId] = useState<number | null | undefined>(
		groups[0]?.parentId,
	);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const parentIds = useMemo(() => {
		const overriddenIds = [...suggestionOverrides.values()]
			.filter((o) => o.parentId !== null)
			.map((o) => String(o.parentId));
		return [
			...new Set([
				...groups.filter((g) => g.parentId !== null).map((g) => String(g.parentId)),
				...overriddenIds,
			]),
		];
	}, [groups, suggestionOverrides]);

	const relatedPageIds = useMemo(
		() =>
			allSuggestions
				.flatMap((s) => s.relatedPages ?? [])
				.map((id) => String(id))
				.filter(Boolean),
		[allSuggestions],
	);

	const contentIdsForTitleLookup = useMemo(
		() => [...new Set([...parentIds, ...relatedPageIds])],
		[parentIds, relatedPageIds],
	);

	const {
		titlesByContentId,
		contentMetaById,
		isLoading: isLoadingTitles,
	} = useContentTitles(contentIdsForTitleLookup);
	const { submitActions } = useSubmitSuggestionActions({ spaceKey, flags });

	const creatorAccountIds = useMemo(
		() => allSuggestions.map((s) => s.creatorAccountId).filter(Boolean) as string[],
		[allSuggestions],
	);
	const creatorInfoMap = useCreatorInfo(creatorAccountIds);

	const hasCompletedInitialLoadRef = useRef(false);
	if (!isFetchingSuggestions && !isLoadingTitles) {
		hasCompletedInitialLoadRef.current = true;
	}

	const isInitialLoading =
		!hasCompletedInitialLoadRef.current && (isFetchingSuggestions || isLoadingTitles);

	// A user-initiated retry from the error state should show the loading view, even though
	// background refetches (post initial load) intentionally do not.
	const [isRetrying, setIsRetrying] = useState(false);
	const isLoading = isInitialLoading || isSubmitting || isRetrying || isReapplyingFilters;
	const hasFetchedSuggestions = groups.some((group) => group.remoteObjects.length > 0);

	const wasFetchingSuggestionsRef = useRef(false);
	useEffect(() => {
		if (isFetchingSuggestions) {
			wasFetchingSuggestionsRef.current = true;
			return;
		}
		if (wasFetchingSuggestionsRef.current || hasFetchedSuggestions) {
			wasFetchingSuggestionsRef.current = false;
			setIsReapplyingFilters(false);
		}
	}, [isFetchingSuggestions, hasFetchedSuggestions]);

	const isFilteredEmpty = !isLoading && !isSuppressed && !hasFetchedSuggestions && isFilterApplied;
	const showErrorState =
		!isLoading && (isSuppressed || (!hasFetchedSuggestions && !isFilterApplied));

	const handleRetry = useCallback(async () => {
		setIsRetrying(true);
		try {
			await refetch();
		} finally {
			setIsRetrying(false);
		}
	}, [refetch]);

	const groupedSuggestionsWithTitles = useMemo(() => {
		const originalGroups = new Map<number | null, ParentGrouping>();
		const newGroups = new Map<number | null, ParentGrouping>();
		const originalParentIds = new Set(groups.map((g) => g.parentId));

		allSuggestions.forEach((suggestion) => {
			const pid = suggestion.parentId;
			const isNewGroup = !originalParentIds.has(pid);
			const target = isNewGroup ? newGroups : originalGroups;
			const existing = target.get(pid);
			if (existing) {
				target.set(pid, { ...existing, suggestions: [...existing.suggestions, suggestion] });
			} else {
				target.set(pid, {
					parentTitle: pid !== null ? (titlesByContentId.get(String(pid)) ?? '') : '',
					suggestions: [suggestion],
				});
			}
		});

		const nullGroup = originalGroups.get(null);
		if (nullGroup) {
			originalGroups.delete(null);
		}
		const result = new Map<number | null, ParentGrouping>([
			...originalGroups.entries(),
			...newGroups.entries(),
		]);
		if (nullGroup) {
			result.set(null, nullGroup);
		}
		return result;
	}, [allSuggestions, titlesByContentId, groups]);

	// The analytics events below log suggestions in the order the user actually sees them, which is
	// the flattened group order (grouped by parent, uncategorized last, moved items relocated) —
	// not the raw fetch order of `allSuggestions`.
	const orderedSuggestions = useMemo(
		() => [...groupedSuggestionsWithTitles.values()].flatMap((group) => group.suggestions),
		[groupedSuggestionsWithTitles],
	);

	const { openTimestamp, fireDismissed, fireAccepted } = useReviewSuggestionsAnalytics({
		spaceId,
		spaceKey,
		orderedSuggestions,
		titlesByContentId,
		selectedItems,
		rejectedItems,
		isLoading,
		isFetchingSuggestions,
		isSuppressed,
		isFilterApplied,
		modalOpenTimestamp,
		reviewSuggestionsExperience,
		filterKey: appliedSources,
	});

	const { trackPreviewChange, flushPreviewAnalytics } = useSuggestionPreviewAnalytics({
		spaceId,
		spaceKey,
	});

	const { fireSuggestionClicked } = useSuggestionClickAnalytics({
		spaceId,
		spaceKey,
	});

	const handleSuggestionHover = useCallback(
		(suggestion: Suggestion | null) => {
			setHoveredSuggestion(suggestion);
			trackPreviewChange(suggestion);
		},
		[trackPreviewChange],
	);

	const handleSuggestionClick = useCallback(
		(suggestion: Suggestion) => {
			fireSuggestionClicked(suggestion);
		},
		[fireSuggestionClicked],
	);

	const handleClose = async () => {
		flushPreviewAnalytics();
		fireDismissed();
		if (rejectedItems.size > 0) {
			const rejectedSuggestions = allSuggestions.filter((s) => rejectedItems.has(s.id));
			await submitActions(rejectedSuggestions, new Set(), rejectedItems, {
				showFlags: false,
			});
			await onClose(true);
		} else {
			await onClose(false);
		}
	};

	const handleAdd = async () => {
		setIsSubmitting(true);
		try {
			const ok = await submitActions(allSuggestions, selectedItems, rejectedItems);
			if (ok) {
				fireAccepted();
				const acceptedParentIds = [
					...new Set(
						allSuggestions
							.filter((s) => selectedItems.has(s.id))
							.map((s) => s.parentId)
							.filter((id): id is number => id !== null),
					),
				];
				await onSaveSuccess(acceptedParentIds);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleMoveSuggestion = useCallback((suggestionId: string, newParentId: number) => {
		setSuggestionOverrides((prev) => new Map(prev).set(suggestionId, { parentId: newParentId }));
		setPreviewParentId(newParentId);
	}, []);

	useEffect(() => {
		if (previewParentId === undefined || groups.length === 0) {
			setPreviewParentId(groups[0]?.parentId);
			return;
		}

		if (!groupedSuggestionsWithTitles.has(previewParentId)) {
			setPreviewParentId(groups[0]?.parentId);
		}
	}, [groups, previewParentId, groupedSuggestionsWithTitles]);

	const handleGroupHoverChange = (parentId: number | null, isHovering: boolean) => {
		setIsHoveringGroupList(isHovering);
		setHoveredParentId(isHovering ? parentId : null);

		if (isHovering) {
			setPreviewParentId(parentId);
		}
	};

	const toggleSelectItem = (itemId: string) => {
		setSelectedItems((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				next.add(itemId);
			}
			return next;
		});
	};

	const toggleRejectItem = (itemId: string) => {
		setRejectedItems((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				// Selecting a reject removes the item from selected
				next.add(itemId);
				setSelectedItems((prevSelected) => {
					const nextSelected = new Set(prevSelected);
					nextSelected.delete(itemId);
					return nextSelected;
				});
			}
			return next;
		});
	};

	const toggleSelectGroup = (groupSuggestions: Suggestion[]) => {
		const allSelected = groupSuggestions.every((s) => selectedItems.has(s.id));
		setSelectedItems((prev) => {
			const next = new Set(prev);
			groupSuggestions.forEach((s) => {
				if (allSelected) {
					next.delete(s.id);
				} else {
					next.add(s.id);
				}
			});
			return next;
		});
	};

	let overlayContent: React.JSX.Element | null = null;
	if (isLoading) {
		overlayContent = (
			<Box xcss={styles.loadingOverlay} testId="review-content-suggestions-modal-loading">
				<Spinner
					size="large"
					label={isSubmitting ? formatMessage(i18n.submitting) : formatMessage(i18n.loading)}
				/>
			</Box>
		);
	} else if (showErrorState) {
		overlayContent = (
			<ModalErrorState
				onClose={handleClose}
				onRetry={handleRetry}
				spaceId={spaceId}
				spaceKey={spaceKey}
				reason={isSuppressed ? 'notProvisioned' : 'empty'}
				openTimestamp={openTimestamp}
			/>
		);
	}

	return (
		<Modal onClose={handleClose} width="auto">
			{overlayContent ?? (
				<Flex xcss={styles.layout}>
					<Box xcss={styles.leftPanel}>
						<Box xcss={styles.leftPanelHeader}>
							<Stack space="space.100">
								<Heading size="large" as="h1">
									{formatMessage(i18n.bringTogetherTitle)}
								</Heading>
								<Text color="color.text.subtlest">
									{formatMessage(i18n.bringTogetherDescription)}
								</Text>
							</Stack>
						</Box>
						<Box xcss={styles.groupListScrollArea}>
							<Stack space="space.100">
								<ModalLeftPanel
									groupedSuggestionsWithTitles={groupedSuggestionsWithTitles}
									contentMetaById={contentMetaById}
									selectedItems={selectedItems}
									rejectedItems={rejectedItems}
									toggleSelectGroup={toggleSelectGroup}
									toggleSelectItem={toggleSelectItem}
									onRejectItem={toggleRejectItem}
									onGroupHoverChange={handleGroupHoverChange}
									onMoveSuggestion={handleMoveSuggestion}
									onSuggestionHover={handleSuggestionHover}
									onSuggestionClick={handleSuggestionClick}
									spaceId={spaceId}
									spaceKey={spaceKey}
									titlesByContentId={titlesByContentId}
									onFilterClose={handleFilterPopupClose}
									filterResetKey={filterResetKey}
									isFilteredEmpty={isFilteredEmpty}
									onClearFilters={clearFilters}
								/>
							</Stack>
						</Box>
						<Flex justifyContent="end">
							<Button
								appearance="primary"
								onClick={() => void handleAdd()}
								isDisabled={selectedItems.size === 0}
							>
								{selectedItems.size > 0
									? formatMessage(i18n.addItemsToSpace, { count: selectedItems.size })
									: formatMessage(i18n.addToThisSpace)}
							</Button>
						</Flex>
					</Box>
					<ModalRightPanel
						onClose={handleClose}
						previewImageUrl={
							hoveredSuggestion
								? (loomThumbnails.get(hoveredSuggestion.id) ?? hoveredSuggestion.iconUrl)
								: undefined
						}
						previewImageLoading={
							isLoomLoading &&
							hoveredSuggestion !== null &&
							hoveredSuggestion.id.includes('::video/') &&
							!loomThumbnails.has(hoveredSuggestion.id)
						}
						previewTitle={hoveredSuggestion?.title}
						previewDescription={hoveredSuggestion?.description}
						previewProductIconUrl={
							hoveredSuggestion
								? getSuggestionIconUrlFromContentUrl(hoveredSuggestion.url)
								: undefined
						}
						previewSourceIconUrl={
							previewSource ? PREVIEW_SOURCE_ICON_URLS[previewSource] : undefined
						}
						previewSource={previewSource}
						previewAuthorName={
							hoveredSuggestion?.creatorAccountId
								? creatorInfoMap.get(hoveredSuggestion.creatorAccountId)?.displayName
								: undefined
						}
						previewAuthorAvatarUrl={
							hoveredSuggestion?.creatorAccountId
								? creatorInfoMap.get(hoveredSuggestion.creatorAccountId)?.avatarUrl
								: undefined
						}
					/>
				</Flex>
			)}
		</Modal>
	);
};

type ReviewContentSuggestionsModalProps = {
	spaceKey?: string;
	spaceId?: string;
	modalOpenTimestamp?: number;
	onClose: (hadRejections: boolean) => void | Promise<void>;
	onSaveSuccess: (acceptedParentIds: number[]) => void | Promise<void>;
};

export const ReviewContentSuggestionsModal = (
	props: ReviewContentSuggestionsModalProps,
): React.JSX.Element => (
	<Subscribe to={[FlagsStateContainer]}>
		{(flags: FlagsStateContainer) => (
			<ReviewContentSuggestionsModalInner {...props} flags={flags} />
		)}
	</Subscribe>
);
